import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { DailyCard } from '../DailyCard';
import { MacroDonut } from '../MacroDonut';
import { useUserTimezone } from '../../../hooks/useUserTimezone';
import { onHealthDataUpdated, emitHealthDataUpdated } from '../../../utils/healthDataEvents';
import type { DailyMetricsData } from '../dailyMetrics';

interface TodayMacros {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
}

/**
 * HomeTodayCard — the mobile Home "today glance" zone.
 *
 * - Fetches today's daily record and renders DailyCard with showScore=false
 *   (today is in-progress → show progress toward targets, not a settled grade).
 * - On mount, triggers a self-scoped FatSecret smart-sync (guarded to once/hour
 *   per session) so the client's own app usage keeps MC's data fresh — the
 *   client is the high-frequency actor, so this is the primary sync catalyst.
 * - Listens for the health-data-updated event (sync complete, manual entry,
 *   manual refresh) and re-fetches.
 */

const SYNC_GUARD_MS = 60 * 60 * 1000; // 1 hour
const SYNC_GUARD_KEY = 'home_fs_smart_synced_at';

export const HomeTodayCard: React.FC = () => {
  const { getToday } = useUserTimezone();
  const [record, setRecord] = useState<DailyMetricsData | null>(null);
  const [macros, setMacros] = useState<TodayMacros | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const didInit = useRef(false);

  const fetchToday = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const today = getToday();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const [recordRes, nutritionRes] = await Promise.all([
        fetch(`/api/health/daily-record/${today}`, { headers }),
        fetch(`/api/nutrition/daily/${today}`, { headers }),
      ]);

      const recordData = await recordRes.json();
      if (recordData.success && recordData.record) {
        setRecord({ ...recordData.record, date: recordData.record.date || today });
      }

      const nutritionData = await nutritionRes.json();
      if (nutritionData.success && nutritionData.data) {
        const n = nutritionData.data;
        setMacros({
          calories: n.calories || 0,
          protein_g: n.protein_g || 0,
          carbs_g: n.carbs_g || 0,
          fats_g: n.fats_g || 0,
        });
      } else {
        setMacros(null);
      }
    } catch (err) {
      console.error('[HomeTodayCard] Failed to fetch today data:', err);
    } finally {
      setLoading(false);
    }
  }, [getToday]);

  const runSmartSync = useCallback(async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/nutrition/me/smart-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ lookbackDays: 14 }),
      });
      if (res.ok) {
        sessionStorage.setItem(SYNC_GUARD_KEY, String(Date.now()));
        // Persisted data may have changed → tell listeners (incl. this card) to refetch.
        emitHealthDataUpdated();
      }
    } catch (err) {
      console.error('[HomeTodayCard] smart-sync failed:', err);
    } finally {
      setSyncing(false);
    }
  }, []);

  // Manual refresh: force a sync regardless of the staleness guard.
  const handleManualRefresh = useCallback(() => {
    if (syncing) return;
    runSmartSync();
  }, [syncing, runSmartSync]);

  // Re-fetch whenever health data changes (sync complete, manual entry, refresh).
  useEffect(() => onHealthDataUpdated(fetchToday), [fetchToday]);

  // On mount: fetch today's record, then auto smart-sync if stale (>1h).
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    fetchToday();

    const last = Number(sessionStorage.getItem(SYNC_GUARD_KEY) || 0);
    if (Date.now() - last > SYNC_GUARD_MS) {
      runSmartSync();
    }
  }, [fetchToday, runSmartSync]);

  if (loading) {
    return <div className="mc-home-today-loading">Loading today…</div>;
  }

  return (
    <div className="mc-home-today">
      <button
        className="mc-home-today-refresh"
        onClick={handleManualRefresh}
        disabled={syncing}
        aria-label="Refresh today's data"
      >
        <RefreshCw size={16} className={syncing ? 'mc-spin' : ''} />
      </button>
      {record ? (
        <DailyCard data={record} showScore={false} showWeight={false} showDate />
      ) : (
        <div className="mc-daily-card">
          <div className="mc-daily-card-header">
            <span className="mc-daily-card-date">Today</span>
          </div>
          <div className="mc-daily-card-empty">No data logged yet today.</div>
        </div>
      )}
      {macros && (macros.protein_g > 0 || macros.carbs_g > 0 || macros.fats_g > 0) && (
        <div className="mc-daily-card mc-home-macros">
          <div className="mc-daily-card-header">
            <span className="mc-daily-card-date">Macros</span>
          </div>
          <MacroDonut
            proteinG={macros.protein_g}
            carbsG={macros.carbs_g}
            fatsG={macros.fats_g}
            calories={macros.calories}
          />
        </div>
      )}
    </div>
  );
};
